import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, combineLatest } from 'rxjs';
import { OfferService } from '../../_services/offer.service';
import { IPagination } from '../../_models/pagination';
import { CartService } from '../../_services/cart.service';
import { StoreService } from '../../_services/store.service';
import { WishlistService } from '../../_services/wishlist.service';
import { ProductCategoryService } from '../../_services/product-category.service';
import { User } from '../../_models/user';
import { Offer, getPrice, isOfferInPromotion } from '../../_models/offer';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-category-products',
    templateUrl: './category-products.component.html',
    styleUrls: ['./category-products.component.css'],
    standalone: false
})
export class CategoryProductsComponent implements OnInit, OnDestroy {

    offers: Offer[];
    recommendedOffers: Offer[] = [];
    loading = false;
    page: any;
    config: any;
    slug: string;
    q: string = null;
    categoryId: number = null;
    user: User = null;
    lang = 'fr';
    title = 'Category Products';
    subscriptions: Subscription[] = [];

    constructor(
        private offerService: OfferService,
        private activatedRoute: ActivatedRoute,
        private cartService: CartService,
        private storeService: StoreService,
        private wishlistService: WishlistService,
        private titleService: Title,
        private toastrService: ToastrService,
        private router: Router,
        private translateService: TranslateService,
        private pcService: ProductCategoryService
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        this.lang = translateService.currentLang;
    }

    ngOnInit() {
        this.loading = true;
        const routesSubscription = combineLatest([
            this.activatedRoute.paramMap,
            this.activatedRoute.queryParamMap
        ]).subscribe(([params, queryParams]) => {
            this.slug = params.get('slug') || null;
            this.page = queryParams.get('page') || '1';
            this.q = queryParams.get('q') || null;

            this.loading = true;
            if (this.slug === 'all') {
                this.getAllOffers();
            } else if (this.slug) {
                if (this.q) {
                    this.loadCategoryIdAndSearch();
                } else {
                    this.getOfferByCategory();
                }
            }
        });
        this.subscriptions.push(routesSubscription);
    }

    loadCategoryIdAndSearch() {
        this.pcService.get_product_categories().subscribe(categories => {
            const cat = categories.find(c => c.slug === this.slug);
            if (cat) {
                this.categoryId = cat.id;
                this.title = cat.nameFr || cat.name;
                this.searchInCategory();
            } else {
                this.getOfferByCategory();
            }
        });
    }

    searchInCategory() {
        this.recommendedOffers = [];
        this.offerService.searchInCategory(this.q, this.categoryId, this.page)
            .subscribe(
                (result: IPagination) => {
                    this.setConfig(result);
                    this.titleService.setTitle(this.q + ' | ' + this.title + ' | eFarm.cm');
                },
                () => {},
                () => {
                    this.loading = false;
                    if (this.offers.length === 0) {
                        this.offerService.loadOffersByCategory(this.slug, 1)
                            .subscribe((result: any) => {
                                this.recommendedOffers = result.data.slice(0, 8);
                            });
                    }
                }
            );
    }

    getOfferByCategory() {
        this.offerService.loadOffersByCategory(this.slug, this.page)
            .subscribe(
                (result: any) => {
                    this.setConfig(result);
                    const catName = result.data.length > 0
                        ? (this.lang === 'fr' && result.data[0].product.category.nameFr
                            ? result.data[0].product.category.nameFr
                            : result.data[0].product.category.name)
                        : this.slug;
                    this.title = catName;
                    this.titleService.setTitle(catName + ' en vente | eFarm.cm');
                },
                () => {},
                () => this.loading = false
            );
    }

    getAllOffers() {
        this.offerService.loadLastProductsByPricing('all', this.page)
            .subscribe(
                (result: IPagination) => {
                    this.setConfig(result);
                    this.title = this.translateService.instant('common.all_products');
                    this.titleService.setTitle('All Products | eFarm.cm');
                },
                () => {},
                () => this.loading = false
            );
    }

    pageChange(page) {
        this.page = page;
        this.loading = true;
        if (this.slug === 'all') {
            this.router.navigate(['products', 'category', 'all'], { queryParams: { page } });
            this.getAllOffers();
        } else if (this.q) {
            this.router.navigate(['products', 'category', this.slug], { queryParams: { q: this.q, page } });
            this.searchInCategory();
        } else {
            this.router.navigate(['products', 'category', this.slug], { queryParams: { page } });
            this.getOfferByCategory();
        }
    }

    getImageSrc(offer: Offer) {
        return environment.image_cdn_url + '/' +
            (offer.defaultPicture.length > 0 ? offer.defaultPicture + '?func=crop&width=252&height=252' : offer.product.picture);
    }

    isInPromotion(offer: Offer): boolean {
        return isOfferInPromotion(offer);
    }

    getOfferPrice(offer: Offer): number {
        return getPrice(offer);
    }

    isOfferInCart(offer) {
        return this.storeService.findItem(offer.id);
    }

    addToCart(offer) {
        this.cartService.addToLocalCart(offer)
            .subscribe(() => {
                const addCartMessage = this.translateService.get('common.message_for_added_to_cart')
                    .subscribe(message => {
                        this.toastrService.success(message);
                    });
                this.subscriptions.push(addCartMessage);
            });
    }

    stripHtml(html: string): string {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').trim();
    }

    addToWishlist(offerId: number) {
        this.wishlistService.addToWishList(this.user.id, offerId).subscribe();
    }

    private setConfig(result: any) {
        this.offers = result.data;
        this.config = {
            currentPage: result.page,
            itemsPerPage: result.itemPerPage,
            totalItems: result.totalOfItems,
            totalOfPage: result.totalOfPage,
            id: 'listing'
        };
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

}
